/**
 * PIN Cryptographic Utilities
 *
 * Provides secure PIN hashing and verification using PBKDF2.
 * Uses Web Crypto API for cross-platform compatibility (Browser, Electron, Capacitor).
 *
 * @since 1.0.0
 */

const PBKDF2_ITERATIONS = 100000
const SALT_LENGTH = 16
const HASH_LENGTH = 32

/**
 * The forbidden default PIN. It must never unlock a surface and must never be
 * stored as a usable secret (ITSiRL §75b SGB V).
 *
 * @since 1.0.0
 * @example
 * ```ts
 * DEFAULT_PIN // "0000"
 * ```
 */
export const DEFAULT_PIN = '0000'

/**
 * True when a PIN is the forbidden default `0000`.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * isDefaultPin('0000') // true
 * isDefaultPin('1234') // false
 * ```
 */
export function isDefaultPin(pin: string): boolean {
  return pin.trim() === DEFAULT_PIN
}

/**
 * Generate a cryptographically secure random salt
 */
function generateSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(SALT_LENGTH))
}

/**
 * Convert Uint8Array to base64 string
 */
function arrayToBase64(arr: Uint8Array): string {
  return btoa(String.fromCharCode(...arr))
}

/**
 * Convert base64 string to Uint8Array
 */
function base64ToArray(base64: string): Uint8Array {
  const binary = atob(base64)
  const arr = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    arr[i] = binary.charCodeAt(i)
  }
  return arr
}

/**
 * Derive a key from PIN using PBKDF2
 */
async function deriveKey(pin: string, salt: Uint8Array): Promise<Uint8Array> {
  const encoder = new TextEncoder()
  const pinData = encoder.encode(pin)

  const keyMaterial = await crypto.subtle.importKey('raw', pinData, 'PBKDF2', false, ['deriveBits'])

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt.buffer as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256'
    },
    keyMaterial,
    HASH_LENGTH * 8
  )

  return new Uint8Array(derivedBits)
}

/**
 * Stored PIN hash format: "salt:hash" (both base64 encoded)
 */
export interface PinHash {
  salt: string
  hash: string
}

/**
 * Hash a PIN for secure storage
 *
 * @param pin - The plaintext PIN to hash
 * @returns A PinHash object containing salt and hash (both base64)
 *
 * @example
 * ```ts
 * const pinHash = await hashPin('1234')
 * // Store pinHash.salt and pinHash.hash
 * ```
 */
export async function hashPin(pin: string): Promise<PinHash> {
  const salt = generateSalt()
  const hash = await deriveKey(pin, salt)

  return {
    salt: arrayToBase64(salt),
    hash: arrayToBase64(hash)
  }
}

/**
 * Verify a PIN against a stored hash
 *
 * @param pin - The plaintext PIN to verify
 * @param storedHash - The stored PinHash to verify against
 * @returns true if PIN matches, false otherwise
 *
 * @example
 * ```ts
 * const isValid = await verifyPinHash('1234', storedPinHash)
 * ```
 */
export async function verifyPinHash(pin: string, storedHash: PinHash): Promise<boolean> {
  const salt = base64ToArray(storedHash.salt)
  const expectedHash = base64ToArray(storedHash.hash)
  const actualHash = await deriveKey(pin, salt)

  // Constant-time comparison to prevent timing attacks
  if (expectedHash.length !== actualHash.length) {
    return false
  }

  let result = 0
  for (let i = 0; i < expectedHash.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    result |= expectedHash[i]! ^ actualHash[i]!
  }

  return result === 0
}

/**
 * Serialize PinHash to a single string for storage
 *
 * @example
 * ```ts
 * const serialized = serializePinHash(pinHash)
 * // "salt:hash"
 * ```
 */
export function serializePinHash(pinHash: PinHash): string {
  return `${pinHash.salt}:${pinHash.hash}`
}

/**
 * Deserialize a stored PIN hash string
 *
 * @example
 * ```ts
 * const pinHash = deserializePinHash(storedString)
 * ```
 */
export function deserializePinHash(serialized: string): PinHash | null {
  const parts = serialized.split(':')
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return null
  }
  return {
    salt: parts[0],
    hash: parts[1]
  }
}

/**
 * Check if a string looks like a hashed PIN (contains salt:hash format)
 * Used for migration detection from plaintext to hashed PINs.
 */
export function isHashedPin(value: string): boolean {
  if (!value.includes(':')) {
    return false
  }
  const pinHash = deserializePinHash(value)
  if (!pinHash) {
    return false
  }
  // Validate base64 format and expected lengths
  try {
    const salt = base64ToArray(pinHash.salt)
    const hash = base64ToArray(pinHash.hash)
    return salt.length === SALT_LENGTH && hash.length === HASH_LENGTH
  } catch {
    return false
  }
}
